<template>
  <div
    data-grid="column"
    class="basicinput"
    :class="$attrs.class"
  >
    <label
      v-if="$hasSlot('group-title') || title"
      data-grid
      :for="`input-${name}`"
      class="basicinput__inputlabel grid--va-center mb-2"
    >
      <slot name="group-title">
        {{ title }}
      </slot>

      <span
        v-if="showMaxlength"
        class="basicinput__length ms-auto"
      >
        {{ fieldLength }} / {{ maxlength }}
      </span>
    </label>

    <slot
      :id="`input-${name}`"
      name="group-input"
      :set-value="setValue"
      :maxlength="maxlength"
      :field-name="name"
      :value="fieldValue"
    >
      <input
        v-bind="$attrs"
        :id="`input-${name}`"
        v-model="fieldValue"
        :name="name"
        :type="type"
        autocomplete="off"
        :required="required"
        :inputmode="inputMode"
        :maxlength="maxlength"
        :placeholder="placeholder"
        class="basicinput__input p-2 w-100 mw-100"
        :class="{ 'basicinput__input--noappearance': noAppearance }"
        @keydown="cleanError"
        @keyup.enter="removeFocus($event)"
        @focus="handleFocus"
        @blur="handleBlur"
      >
    </slot>

    <small class="basicinput-error" />
  </div>
</template>

<script>
import { defineComponent } from 'vue'

export default defineComponent({
  name: 'BasicInput',

  inheritAttrs: false,

  props: {
    name: {
      type: String,
      default: () => ''
    },

    placeholder: {
      type: String,
      default: () => ''
    },

    modelValue: {
      type: [String, Number],
      default: () => ''
    },

    type: {
      type: String,
      default: () => 'text'
    },

    inputMode: {
      type: String,
      default: () => 'text'
    },

    title: {
      type: String,
      default: () => ''
    },

    required: Boolean,

    showMaxlength: {
      type: Boolean
    },

    maxlength: {
      type: Number,
      default: () => Infinity
    },

    noAppearance: Boolean
  },

  emits: [
    'update:modelValue', 'input', 'on-focus', 'on-blur'
  ],

  computed: {
    fieldLength () {
      return this.modelValue.length
    },

    fieldValue: {
      get () {
        return this.modelValue
      },

      set (v) {
        this.emmitValues(String(v).substring(0, this.maxlength))
      }
    }
  },

  mounted () {
    this.emitter.on(`on-${this.name}-input-error`, this.onError)
    this.emitter.on(`on-${this.name}-input-readonly`, this.setReadOnly)
  },

  unmounted () {
    this.emitter.off(`on-${this.name}-input-error`, this.onError)
    this.emitter.off(`on-${this.name}-input-readonly`, this.setReadOnly)
  },

  methods: {
    setReadOnly (value) {
      this.readOnly = value
    },

    /**
     * Limpa os elementos com erro
     */
    cleanError () {
      const error = this.$el.querySelector('.basicinput-error')
      error.innerText = ''

      const input = this.$el.querySelector('input, select')
      input?.classList.remove('input--error')
    },

    removeFocus (ev) {
      const inputFocus = ev.target
      inputFocus.blur()
    },

    emmitValues (value) {
      this.$emit('update:modelValue', value)
      this.$emit('input', value)
    },

    onError (message) {
      if (message === '') {
        return this.cleanError()
      }
      const input = this.$el.querySelector('input, select')
      const error = this.$el.querySelector('.basicinput-error')

      error.innerText = message.replace(` ${this.name}`, '')
      if (!input.classList.contains('input--error')) {
        input.classList.add('input--error')
      }
    },

    setValue (value) {
      this.fieldValue = value
    },

    handleFocus () {
      this.$emit('on-focus')
    },

    handleBlur () {
      this.$emit('on-blur')
    }
  }
})
</script>

<style lang="scss" scoped>
  .basicinput {
    transition: .2s ease-in-out;

    &-error {
      margin-top: 3px;
      color: var(--error);
      white-space: nowrap;

      &:empty {
        display: none;
      }
    }

    &.disabled {
      opacity: .7;
      cursor: not-allowed;
      pointer-events: none;

      input {
        background: rgb(var(--gray6));
      }
    }

    &__inputlabel {
      color: rgb(var(--gray8));
      font: 700 15px "Nunito", sans-serif;

      .is-dark-theme & {
        font-weight: 400;
        color: rgb(var(--gray6));
      }
    }

    &__length {
      color: rgb(var(--gray5));
      font: 400 .9rem / 2rem "Nunito", sans-serif;
    }

    :slotted(input),
    :slotted(select) {
      flex-grow: 1;
      font: 400 1.4rem "Nunito", sans-serif;
      border: solid var(--borderSize, 1px) var(--borderColor, rgb(var(--gray7)));

      .is-dark-theme & {
        --borderSize: 0;

        color: rgb(var(--white));
        background-color: rgb(var(--gray8));

        &::placeholder {
          color: rgba(var(--white), .5);
        }
      }
    }

    &__input {
      height: 42px;
      flex-basis: 42px;
      border-radius: var(--borderRadius, 4px);

      &--noappearance {
        &::-webkit-outer-spin-button,
        &::-webkit-inner-spin-button {
          -webkit-appearance: none;
        }

        /* Firefox */
        &[type=number] {
          -moz-appearance: textfield;
        }
      }

      &.input--error {
        border: 1px solid var(--error);
      }

      .rounded & {
        border-radius: 24px;
      }

      .inputdropdown & {
        --borderSize: 2px;

        height: 52px;
        flex-basis: 52px;
        border: solid var(--borderWidth) var(--borderColor);
      }
    }
  }
</style>
